import { ApolloClient, gql } from '@apollo/client';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ConversationParticipant } from '@provision/entity-types';
import {
  CLIENT_FIELDS,
  CREATE_PARTICIPANT_MUTATION_QUERY,
  CreateConversationParticipantMutation,
} from 'src/graphql';
import { ADDRESS_FIELDS } from 'src/graphql/entities/address/address.fragments';
import { CLIENT_EMAIL_FIELDS } from 'src/graphql/entities/client-email';
import { CLIENT_PHONE_FIELDS } from 'src/graphql/entities/client-phone';
import { Records } from 'src/graphql/types';

const GET_EXISTING_PARTICIPANT_QUERY = gql`
  query GetExistingConversationParticipant($params: QueryParams) {
    getConversationParticipant(queryParams: $params) {
      records {
        id
        partnerId
        conversationParticipantId
        participantIdentity
        participantEntity
        client {
          ...ClientFields
          primaryEmail {
            ...ClientEmailFields
          }
          emails {
            ...ClientEmailFields
          }
          phoneNumbers {
            ...ClientPhoneFields
          }
          addresses {
            ...AddressFields
          }
        }
        conversationChannels {
          id
          externalIdentifier
          conversationChannelTypeId
        }
        platformUser {
          id
          userId
          partnerId
          firstName
          lastName
          email
          createdAt
          updatedAt
          phoneNumber
          isActive
        }
      }
    }
  }
  ${CLIENT_FIELDS}
  ${CLIENT_EMAIL_FIELDS}
  ${CLIENT_EMAIL_FIELDS}
  ${CLIENT_PHONE_FIELDS}
  ${ADDRESS_FIELDS}
`;

interface GetExistingParticipantQuery {
  getConversationParticipant: Records<Partial<ConversationParticipant>>;
}

/**
 * Get or create a ConversationParticipant ID for the given entity (platform_user or client)
 * Will also update the conversation address if the entity is a client
 * @returns The ConversationParticipant
 */
export const getConversationParticipantForEntity = async ({
  apolloClient,
  partnerId,
  entityId,
  participantEntity,
}: {
  apolloClient: ApolloClient<object>;
  partnerId: number;
  entityId: string;
  participantEntity: ConversationParticipantEntity;
}): Promise<Partial<ConversationParticipant> | undefined> => {
  if (!entityId) {
    return;
  }
  const {
    data: {
      getConversationParticipant: {
        records: [existingParticipant],
      },
    },
  } = await apolloClient.query<GetExistingParticipantQuery>({
    query: GET_EXISTING_PARTICIPANT_QUERY,
    variables: {
      params: {
        filters: [
          {
            attribute: 'participantIdentity',
            operator: '=',
            value: entityId,
          },
        ],
        limit: 1,
      },
    },
  });

  let participant: Partial<ConversationParticipant> | undefined = existingParticipant;

  if (!participant) {
    const { data } = await apolloClient.mutate<CreateConversationParticipantMutation>({
      mutation: CREATE_PARTICIPANT_MUTATION_QUERY,
      variables: {
        partnerId,
        participantEntity,
        participantIdentity: entityId,
      },
    });
    participant = data?.createConversationParticipant?.[0];
  }

  return participant;
};
